import { useEffect, useState } from 'react'
import { Search, AlertCircle, Timer, SparklesIcon, ChevronDown, ChevronUp, SlidersHorizontal, Info } from 'lucide-react'
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { ConvergenceCard } from "./convergence-card"
import { FilterControls } from "./filter-controls"
import { Tables } from '../../../database.types'
import { supabase } from '../../lib/supabase'
import { useBrands } from '@/lib/BrandsProvider'
import NoBrandSelected from '../NoBrandSelected'
import { Link } from 'react-router-dom'
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type Convergence = Tables<'convergences'>
type NewsContent = Tables<'news_contents'>
type StatusFilter = "Used" | "Unused" | "Unranked" | "Archived" | "all";
type ExpirationFilter = "fresh" | "expired" | "all";

// Pagination constants
const ITEMS_PER_PAGE = 12;

interface ConvergenceWithRelations extends Convergence {
  brands: {
    id: string;
    brand_name: string;
  };
  news_contents?: NewsContent;
}


export default function Convergences() {
  const { selectedBrand } = useBrands()
  const { toast } = useToast()
  // navigate not used
  const [convergences, setConvergences] = useState<ConvergenceWithRelations[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [expirationFilter, setExpirationFilter] = useState<ExpirationFilter>('fresh')
  const [sortBy, setSortBy] = useState('most-recent')
  const [notifyOnConvergences, setNotifyOnConvergences] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [filtersExpanded, setFiltersExpanded] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  const fetchConvergences = async (filter: StatusFilter, sort: string) => {
    if (!selectedBrand) return []

    let query = supabase
      .from('convergences')
      .select(`
        *,
        brands!inner (
          id,
          brand_name
        ),
        news_contents (
          id,
          name,
          description,
          trend_analysis,
          business_opportunities_analysis,
          audience_and_brand_analysis,
          raw_headline,
          raw_content,
          authoritative_urls,
          interesting_links
        )
      `)
      .eq('brand_id', selectedBrand.id)

    if (filter !== 'all') {
      query = query.eq('status', filter);
    }

    switch (sort) {
      case 'most-recent':
        query = query.order('created_at', { ascending: false });
        break;
      case 'virality':
        query = query.order('virality_score', { ascending: false, nullsFirst: false });
        break;
      case 'relevance':
        query = query.order('relevance_score', { ascending: false, nullsFirst: false });
        break;
      case 'power':
        query = query.order('power_score', { ascending: false, nullsFirst: false });
        break;
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching convergences:', error);
      throw error;
    }

    return data as unknown as ConvergenceWithRelations[];
  };

  useEffect(() => {
    if (selectedBrand) {
      setNotifyOnConvergences(selectedBrand.notify_on_convergences ?? false);
    }
  }, [selectedBrand]);

  // Detect mobile viewport and set initial filter state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Only collapse filters on mobile, expand on desktop
      if (mobile && filtersExpanded) {
        setFiltersExpanded(false);
      } else if (!mobile && !filtersExpanded) {
        setFiltersExpanded(true);
      }
    };

    // Check on mount
    checkMobile();

    // Add resize listener
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleNotificationPreference = async (checked: boolean) => {
    if (!selectedBrand) return;
    
    try {
      const { error } = await supabase
        .from('brands')
        .update({ 
          notify_on_convergences: checked,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedBrand.id);
      
      if (error) throw error;
      setNotifyOnConvergences(checked);
      
      toast({
        title: checked ? "Notifications enabled" : "Notifications disabled",
        description: checked 
          ? "You'll be notified when new convergences are available" 
          : "You won't be notified about new convergences",
      });
    } catch (error) {
      console.error('Error updating notification preference:', error);
      toast({
        title: "Error",
        description: "Failed to update notification preference",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const loadConvergences = async () => {
      if (!selectedBrand) return;
      
      try {
        setIsLoading(true);
        const data = await fetchConvergences(statusFilter, sortBy);
        setConvergences(data || []);
      } catch (error) {
        console.error('Failed to load convergences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the loadConvergences call to prevent duplicate queries
    const timeoutId = setTimeout(loadConvergences, 100);

    if (selectedBrand) {
      const subscription = supabase
        .channel('convergences')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'convergences',
            filter: `brand_id=eq.${selectedBrand.id}`
          },
          () => {
            // Refetch to get the full data with relations
            loadConvergences();
          }
        )
        .subscribe()

      return () => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();
      }
    }

    return () => clearTimeout(timeoutId);
  }, [statusFilter, sortBy, selectedBrand]);

  // Update FilterControls to batch state updates
  const handleFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setExpirationFilter('fresh');
    setSortBy('most-recent');
  };
  
  const handleExpirationChange = (expiration: ExpirationFilter) => {
    setExpirationFilter(expiration);
  };

  const filteredConvergences = convergences.filter(convergence => {
    // Search filter
    const matchesSearch = convergence.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      convergence.trend?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      convergence.convergence?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Expiration filter
    const now = new Date();
    const expirationDate = convergence.expiration_date ? new Date(convergence.expiration_date) : null;
    let matchesExpiration = true;
    
    if (expirationFilter === 'fresh') {
      matchesExpiration = !expirationDate || expirationDate > now;
    } else if (expirationFilter === 'expired') {
      matchesExpiration = expirationDate ? expirationDate <= now : false;
    }
    // 'all' shows everything
    
    return matchesSearch && matchesExpiration;
  })
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredConvergences.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedConvergences = filteredConvergences.slice(startIndex, endIndex);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, expirationFilter, sortBy]);

  if (!selectedBrand) {
    return <NoBrandSelected />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
        
        {/* Enhanced Header Section with Gradient Background */}
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-4 md:p-8">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
          <div className="relative">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 md:gap-6">
              <div className="flex items-center gap-2 md:gap-4">
                <div className="p-2 md:p-3 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl shadow-lg">
                  <SparklesIcon className="h-5 w-5 md:h-8 md:w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 md:mb-2">
                    <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">Convergences</h1>
                    {/* Tooltip icon - visible on all screen sizes */}
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            type="button"
                            className="p-1.5 rounded-full hover:bg-blue-100/50 transition-colors flex-shrink-0"
                            aria-label="Convergences information"
                          >
                            <Info className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs p-3">
                          <p className="text-sm leading-relaxed">
                            Topical content opportunities to keep your brand trending. Convergences are AI-identified topics that combine multiple news sources and trends relevant to your brand.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {/* Desktop-only description text - mobile users see tooltip instead */}
                  <p className="text-gray-600 text-base md:text-lg leading-relaxed hidden md:block">
                    Topical content opportunities to keep your brand trending
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Alert */}
        <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-purple-50/20" />
          <div className="relative flex items-start sm:items-center gap-3 text-sm p-4">
            <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5 sm:mt-0" />
            <span className="text-gray-700">
              For better accuracy, ensure your brand's target audience and value propositions are configured in{' '}
              <Link to="/brand-genius" className="font-medium text-blue-600 hover:text-blue-700 underline underline-offset-4 transition-colors">
                Brand Genius
              </Link>
            </span>
          </div>
        </div>

        {/* Enhanced Filters Section */}
        <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30" />
          <div className="relative">
            {/* Toggle Button - Only visible on mobile */}
            {isMobile && (
              <button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/30 transition-colors"
                aria-expanded={filtersExpanded}
                aria-label="Toggle filters"
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-gray-700">
                    {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
                  </span>
                  {!filtersExpanded && (
                    <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded-full">
                      {[statusFilter !== 'all', expirationFilter !== 'all', sortBy !== 'most-recent', searchQuery].filter(Boolean).length} active
                    </span>
                  )}
                </div>
                {filtersExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                )}
              </button>
            )}

            {/* Collapsible Filters Content */}
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search Bar */}
                  <div className="relative flex-grow">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search convergences..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 bg-white/50 border-white/50 focus:bg-white/70 transition-colors"
                    />
                  </div>

                  {/* Filter Controls */}
                  <FilterControls
                    onFilterChange={handleFilterChange}
                    onExpirationChange={handleExpirationChange}
                    onSortChange={handleSortChange}
                    onClearFilters={handleClearFilters}
                    loading={isLoading}
                    currentFilter={statusFilter}
                    currentExpiration={expirationFilter}
                    currentSort={sortBy}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="space-y-8">
          {/* Card View - Grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-fr">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                  <Skeleton className="h-[520px] rounded-xl" />
                </div>
              ))
            ) : filteredConvergences.length === 0 ? (
            <div className="col-span-full">
              <div className="relative overflow-hidden bg-gradient-to-br from-white via-gray-50/30 to-blue-50/20 rounded-2xl border border-gray-200/50 shadow-sm">
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="relative flex flex-col items-center justify-center py-20 px-8">
                  {searchQuery ? (
                    <>
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                        <Search className="h-12 w-12 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">No convergences found</h3>
                      <p className="text-gray-600 text-center mb-6 max-w-md leading-relaxed">
                        Try adjusting your search terms or filters
                      </p>
                    </>
                  ) : convergences.length === 0 ? (
                    <>
                      <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center mb-6 animate-pulse">
                        <Timer className="h-12 w-12 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent mb-2">Looking for convergences...</h3>
                      <p className="text-gray-600 text-center mb-6 max-w-md leading-relaxed">
                        This shouldn't take long, but sometimes it can take up to 24 hours for convergences to show up depending on your brand.
                      </p>
                      <div className="flex items-center justify-center space-x-3 p-4 bg-white/50 rounded-xl">
                        <Checkbox
                          id="notify"
                          checked={notifyOnConvergences}
                          onCheckedChange={handleNotificationPreference}
                          className="border-blue-300"
                        />
                        <Label htmlFor="notify" className="text-sm cursor-pointer text-gray-700 font-medium">
                          Notify me when convergences are available
                        </Label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-6">
                        <Search className="h-12 w-12 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">No matching convergences</h3>
                      <p className="text-gray-600 text-center mb-6 max-w-md leading-relaxed">
                        Try adjusting your filters or search terms
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
            ) : (
              paginatedConvergences.map((convergence) => (
                <ConvergenceCard
                  key={convergence.id}
                  convergence={convergence}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <Pagination>
                <PaginationContent className="gap-2">
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                      className={`${currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:bg-white/70'} bg-white/50 border-white/50 transition-colors`}
                    />
                  </PaginationItem>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 5) {
                      pageNumber = i + 1;
                    } else {
                      const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                      pageNumber = start + i;
                    }
                    
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          isActive={currentPage === pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`cursor-pointer transition-colors ${
                            currentPage === pageNumber 
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-transparent hover:from-blue-600 hover:to-indigo-700' 
                              : 'bg-white/50 border-white/50 hover:bg-white/70'
                          }`}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis className="bg-white/30" />
                    </PaginationItem>
                  )}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                      className={`${currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:bg-white/70'} bg-white/50 border-white/50 transition-colors`}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
